import { clsx } from 'clsx';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';
import { Link } from 'react-router';
import { langProps, textOf } from '../../ui/lang';
import type { IconName } from '../module';
import { Icon } from './Icon';
import { Wordmark } from './Wordmark';

/**
 * The shell's small kit: just enough for the shell screens. Each piece
 * follows BRAND.md (ink outline, hard shadow, flat fill). The cards module owns PlayerCard and
 * the tier shapes; modules build their own components on top of these classes.
 */

export { Icon } from './Icon';
export { Wordmark } from './Wordmark';

type Variant = 'primary' | 'accent' | 'go' | 'danger' | 'plain' | 'ghost';
type Size = 'md' | 'lg' | 'xl';

const VARIANT: Record<Variant, string> = {
  primary: 'tts-btn-primary',
  accent: 'tts-btn-accent',
  go: 'tts-btn-go',
  danger: 'tts-btn-danger',
  plain: '',
  ghost: 'tts-btn-ghost',
};
const SIZE: Record<Size, string> = { md: '', lg: 'tts-btn-lg', xl: 'tts-btn-xl' };

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: IconName;
  block?: boolean;
}

export function Button({ variant = 'plain', size = 'md', icon, block, className, children, ...rest }: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      className={clsx('tts-btn', VARIANT[variant], SIZE[size], block && 'w-full', className)}
    >
      {icon && <Icon name={icon} size={size === 'xl' ? 28 : 22} />}
      <span>{children}</span>
    </button>
  );
}

export function ButtonLink({
  to,
  variant = 'plain',
  size = 'md',
  icon,
  block,
  className,
  children,
}: {
  to: string;
  variant?: Variant;
  size?: Size;
  icon?: IconName;
  block?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link to={to} className={clsx('tts-btn', VARIANT[variant], SIZE[size], block && 'w-full', className)}>
      {icon && <Icon name={icon} size={size === 'xl' ? 28 : 22} />}
      <span>{children}</span>
    </Link>
  );
}

/** Slanted headline plate, the wordmark's angle. */
export function Plate({
  children,
  color = 'white',
  className,
  as: As = 'h1',
}: {
  children: ReactNode;
  color?: 'white' | 'butter' | 'lavender' | 'sky' | 'mint' | 'peach';
  className?: string;
  as?: 'h1' | 'h2' | 'h3' | 'div' | 'span';
}) {
  const bg = {
    white: 'bg-white',
    butter: 'bg-butter',
    lavender: 'bg-lavender',
    sky: 'bg-sky',
    mint: 'bg-mint',
    peach: 'bg-peach',
  }[color];
  return (
    <As className={clsx('tts-plate tts-display text-2xl', bg, className)} {...langProps(textOf(children))}>
      <span>{children}</span>
    </As>
  );
}

/** Empty state with a funny line, for screens with nothing to show yet. */
export function EmptyState({
  title,
  body,
  art = 'stack',
  action,
  className,
}: {
  title: string;
  body?: string;
  art?: 'stack' | 'sticker' | IconName | 'none';
  action?: ReactNode;
  className?: string;
}) {
  return (
    <section className={clsx('tts-card flex flex-col items-center gap-4 px-5 py-7 text-center', className)}>
      {art === 'stack' || art === 'sticker' ? (
        <Wordmark variant={art} width={art === 'stack' ? 150 : 120} />
      ) : art !== 'none' ? (
        <span className="grid size-16 place-items-center rounded-tts border-3 border-ink bg-butter shadow-btn">
          <Icon name={art} size={34} fill="var(--color-white)" />
        </span>
      ) : null}
      <h2 className="tts-display text-2xl">{title}</h2>
      {body && <p className="max-w-[32ch] text-base font-medium text-ink/80">{body}</p>}
      {action}
    </section>
  );
}

/** A labelled on/off switch, 48px tall, ink outline. */
export function Toggle({
  label,
  help,
  checked,
  onChange,
}: {
  label: string;
  help?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  const id = useId();
  return (
    <label htmlFor={id} className="flex min-h-12 cursor-pointer items-center justify-between gap-4 py-2">
      <span className="flex flex-col">
        <span className="font-semibold">{label}</span>
        {help && <span className="text-sm text-ink/70">{help}</span>}
      </span>
      <span className="relative inline-flex shrink-0">
        <input
          id={id}
          type="checkbox"
          role="switch"
          aria-checked={checked}
          className="peer sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="block h-8 w-14 rounded-tts border-3 border-ink bg-white shadow-[3px_3px_0_0_var(--color-ink)] transition-colors peer-checked:bg-mint peer-focus-visible:outline-3 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ink" />
        <span className="absolute top-1.5 left-1.5 block size-5 rounded-chip border-3 border-ink bg-ink transition-transform duration-(--dur-fast) peer-checked:translate-x-6 peer-checked:bg-white" />
      </span>
    </label>
  );
}

export function Field({
  label,
  hint,
  className,
  ...input
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }) {
  const id = useId();
  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-bold uppercase tracking-wide">
        {label}
      </label>
      <input id={id} className="tts-input" {...input} />
      {hint && <p className="text-sm text-ink/70">{hint}</p>}
    </div>
  );
}

/** A section card with a small slanted label on top (Me sections, Admin sections). */
export function Section({ title, children, className }: { title?: string; children: ReactNode; className?: string }) {
  return (
    <section className={clsx('tts-card relative px-4 pt-5 pb-4', title && 'mt-3', className)}>
      {title && (
        <h2 className="tts-plate tts-display absolute -top-4 left-3 bg-butter text-sm">
          <span>{title}</span>
        </h2>
      )}
      {children}
    </section>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <span role="status" className="inline-flex items-center gap-2 font-semibold">
      <span className="animate-spin">
        <Icon name="ball" size={26} fill="var(--color-peach)" />
      </span>
      <span>{label}</span>
    </span>
  );
}

/** Placeholder block while a lazy part loads. */
export function SkeletonCard({ className }: { className?: string }) {
  return <div className={clsx('tts-card-pale h-28 animate-pulse', className)} aria-hidden="true" />;
}
